/**
 * lib/consent/signal.ts — the ONE source of truth for the region-scoped
 * consent signal (FRIDGE-115, Consent Mode v2).
 *
 * Framework-free and DOM-free on purpose: middleware.ts (edge runtime) stamps
 * the region cookie from it, app/google-analytics.tsx interpolates the region
 * list and the granted-cookie test into the inline gtag snippet from it, and
 * lib/consent/client.ts reads the browser state through it. One list, one
 * cookie shape, no drift — pinned by tests/consent-signal.test.ts.
 *
 * How the signal flows:
 *   1. middleware.ts reads `x-vercel-ip-country`; when the country is in
 *      CONSENT_GATED_COUNTRIES it sets the non-identifying cookie
 *      `fk_consent_region=1` on the response (deleted again if a stale one
 *      arrives from a non-gated country). Nothing is set for anyone else, so
 *      the US path never sees a Set-Cookie header.
 *   2. The browser reads that cookie synchronously (no round trip): PostHog
 *      (app/providers.tsx) and Sentry (sentry.client.config.ts) initialise
 *      only when `isTelemetryAllowed` — i.e. not gated, or gated and the
 *      visitor chose Allow. Google Analytics does not need the cookie: the
 *      inline snippet sets a Consent Mode default of denied scoped by
 *      `region:` to the same list, and Google applies it by the visitor's IP.
 *   3. The visitor's choice is stored in `fk_consent=granted|denied` for six
 *      months; the banner (components/consent-banner.tsx) renders only while
 *      gated and undecided.
 */

/**
 * ISO 3166-1 alpha-2 codes where analytics identifiers need consent first:
 * the EU-27, the three EEA EFTA states, the United Kingdom (with Gibraltar),
 * Switzerland (Google's EU user consent policy scope is EEA + UK + CH), and
 * the EU outermost regions that carry their own country code.
 */
export const CONSENT_GATED_COUNTRIES: readonly string[] = [
  // EU-27
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU',
  'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES',
  'SE',
  // EEA EFTA states
  'IS', 'LI', 'NO',
  // United Kingdom and Gibraltar
  'GB', 'GI',
  // Switzerland
  'CH',
  // EU outermost regions with their own ISO code (French overseas
  // departments and Åland)
  'GF', 'GP', 'MQ', 'RE', 'YT', 'AX',
]

const GATED_SET: ReadonlySet<string> = new Set(CONSENT_GATED_COUNTRIES)

/** True when a country code (any case, or null/undefined) is consent-gated. */
export function isConsentGatedCountry(country: string | null | undefined): boolean {
  if (!country) return false
  return GATED_SET.has(country.trim().toUpperCase())
}

/** Cookie the middleware stamps for gated regions. Value is always '1'. */
export const CONSENT_REGION_COOKIE = 'fk_consent_region'
/** Cookie holding the visitor's own choice: 'granted' | 'denied'. */
export const CONSENT_DECISION_COOKIE = 'fk_consent'
/** Six months, the longest choice-record lifetime the EU regulators accept. */
export const CONSENT_DECISION_MAX_AGE_SECONDS = 182 * 24 * 60 * 60
/** The region cookie is refreshed on every response; a day is plenty. */
export const CONSENT_REGION_MAX_AGE_SECONDS = 24 * 60 * 60

export type ConsentDecision = 'granted' | 'denied'

export interface ConsentState {
  /** The middleware marked this visitor's country as consent-gated. */
  gated: boolean
  /** The visitor's stored choice, or null while undecided. */
  decision: ConsentDecision | null
}

/** Read one cookie value out of a `Cookie` header / `document.cookie` string. */
export function readCookieValue(cookieHeader: string | null | undefined, name: string): string | null {
  if (!cookieHeader) return null
  for (const part of cookieHeader.split(';')) {
    const eq = part.indexOf('=')
    if (eq === -1) continue
    if (part.slice(0, eq).trim() !== name) continue
    return part.slice(eq + 1).trim()
  }
  return null
}

export function parseConsentDecision(value: string | null | undefined): ConsentDecision | null {
  return value === 'granted' || value === 'denied' ? value : null
}

/** Derive the full consent state from a cookie string. */
export function readConsentState(cookieHeader: string | null | undefined): ConsentState {
  return {
    gated: readCookieValue(cookieHeader, CONSENT_REGION_COOKIE) === '1',
    decision: parseConsentDecision(readCookieValue(cookieHeader, CONSENT_DECISION_COOKIE)),
  }
}

/**
 * Whether analytics and error monitoring may run for this state: everyone
 * outside the gated regions (unchanged from today), and gated visitors who
 * chose Allow.
 */
export function isTelemetryAllowedFor(state: ConsentState): boolean {
  return !state.gated || state.decision === 'granted'
}

/**
 * Regex source the inline gtag snippet interpolates to detect a stored Allow
 * — kept here so the browser-side test and the unit test are one pattern.
 */
export const CONSENT_GRANTED_COOKIE_REGEX_SOURCE = String.raw`(^|;\s*)fk_consent=granted(;|$)`

/**
 * Consent Mode v2 default for the gated regions: every storage type denied.
 * `region` scopes it — Google applies the default only to visitors it places
 * in these countries, so the US path is unchanged.
 */
export const GTAG_CONSENT_DEFAULT = {
  ad_storage: 'denied',
  analytics_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  region: CONSENT_GATED_COUNTRIES,
} as const

/**
 * What Allow grants. Analytics storage only: the privacy policy states the
 * site serves no advertising and uses no advertising cookies, so the ad
 * signals stay denied and the banner promises exactly what it grants.
 */
export const GTAG_CONSENT_GRANTED_UPDATE = {
  analytics_storage: 'granted',
} as const

/** What Decline (or withdrawal) sends after a prior grant. */
export const GTAG_CONSENT_DENIED_UPDATE = {
  analytics_storage: 'denied',
} as const

/**
 * The consent lines the inline gtag snippet emits, built here so the text the
 * browser receives has ONE author and can be asserted literally by
 * tests/consent-mode-v2.test.ts. (Asserting the component's rendered markup
 * is not possible: next/script emits nothing outside the Next runtime.)
 *
 * Order is the contract — the DEFAULT precedes `gtag('js', new Date())`, and
 * a stored Allow is replayed as an UPDATE right after it so a returning
 * visitor's grant survives the page load.
 */
export function buildGtagConsentLines(): string {
  return [
    `gtag('consent', 'default', ${JSON.stringify(GTAG_CONSENT_DEFAULT)});`,
    `if (/${CONSENT_GRANTED_COOKIE_REGEX_SOURCE}/.test(document.cookie)) {`,
    `  gtag('consent', 'update', ${JSON.stringify(GTAG_CONSENT_GRANTED_UPDATE)});`,
    `}`,
  ].join('\n')
}
